import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import './Review.css';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({
    name: '',
    email: '',
    rating: 5,
    service: '',
    title: '',
    review: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsPerPage, setReviewsPerPage] = useState(5);
  const [sortBy, setSortBy] = useState('newest');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Your Google Apps Script URL
  const SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbxEBr02MqJvjAuRjvgLhy50PD1FXjtwVZvyViYU1rHj10Yqtsu9tHMfgudz1EsLNKGDuA/exec';

  const services = [
    "All Services",
    "Web Development",
    "System Setup & Maintenance",
    "Troubleshooting & Repair",
    "Networking Solutions",
    "IT Consulting",
    "Automation",
    "Network Security",
    "Wireless Networking",
    "Other"
  ];

  // Review Data Calculations - MOVED UP BEFORE structuredData
  const reviewStats = useMemo(() => {
    const totalReviews = reviews.length;
    if (totalReviews === 0) {
      return { 
        averageRating: 0, 
        ratingCounts: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }, 
        ratingPercentages: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 } 
      };
    }

    const ratingCounts = reviews.reduce((acc, review) => {
      acc[review.rating] = (acc[review.rating] || 0) + 1;
      return acc;
    }, { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 });

    const totalRatingSum = reviews.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = totalRatingSum / totalReviews;

    const ratingPercentages = {};
    for (const rating in ratingCounts) {
      ratingPercentages[rating] = Math.round((ratingCounts[rating] / totalReviews) * 100);
    }

    return { averageRating, ratingCounts, ratingPercentages };
  }, [reviews]);

  // Structured Data for Reviews - MOVED AFTER reviewStats
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Client Reviews | Raffcodes",
    "description": "Read what our clients say about Raffcodes' IT solutions, web development, and technology services in Nairobi, Kenya.",
    "url": "https://raffcodes.tech/reviews",
    "about": {
      "@type": "Organization",
      "name": "Raffcodes",
      "aggregateRating": reviews.length > 0 ? {
        "@type": "AggregateRating",
        "ratingValue": reviewStats.averageRating.toFixed(1),
        "reviewCount": reviews.length,
        "bestRating": "5",
        "worstRating": "1"
      } : undefined
    },
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": reviews.slice(0, 10).map((review, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": review.name
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": review.rating,
            "bestRating": "5",
            "worstRating": "1"
          },
          "reviewBody": review.description,
          "datePublished": review.date,
          "itemReviewed": {
            "@type": "Service",
            "name": review.service || "IT Service"
          }
        }
      }))
    }
  };

  // Sort reviews
  const sortedReviews = useMemo(() => {
    let sorted = [...reviews];
    
    switch (sortBy) {
      case 'newest':
        sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
        break;
      case 'oldest':
        sorted.sort((a, b) => new Date(a.date) - new Date(b.date));
        break;
      case 'highest':
        sorted.sort((a, b) => b.rating - a.rating);
        break;
      case 'lowest':
        sorted.sort((a, b) => a.rating - b.rating);
        break;
      case 'name':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      default:
        sorted.sort((a, b) => new Date(b.date) - new Date(a.date));
    }
    
    return sorted;
  }, [reviews, sortBy]);

  // Filter reviews by service
  const filteredReviews = useMemo(() => {
    return activeFilter === 'all' 
      ? sortedReviews 
      : sortedReviews.filter(review => review.service === activeFilter);
  }, [sortedReviews, activeFilter]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = filteredReviews.slice(indexOfFirstReview, indexOfLastReview);

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const startPage = Math.max(1, currentPage - 2);
      const endPage = Math.min(totalPages, currentPage + 2);
      
      if (startPage > 1) pageNumbers.push(1);
      if (startPage > 2) pageNumbers.push('...');
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (endPage < totalPages - 1) pageNumbers.push('...');
      if (endPage < totalPages) pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  // Fetch reviews using CORS proxy
  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const proxyUrl = 'https://corsproxy.io/?';
      const targetUrl = `${SCRIPT_URL}?action=getReviews&t=${Date.now()}`;
      
      const response = await fetch(proxyUrl + encodeURIComponent(targetUrl), {
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.success && Array.isArray(data.reviews)) {
          const formattedReviews = data.reviews.map((review, index) => {
            const timestamp = review.Timestamp || review.timestamp;
            const date = timestamp ? 
                new Date(timestamp) : 
                new Date();
            
            let rating = parseInt(review.Rating || review.rating);
            if (isNaN(rating) || rating < 1 || rating > 5) {
              rating = 5;
            }

            const reviewerName = review.Name || review.name || 'Anonymous';
            const service = review.Service || review.service || 'General Service';

            return {
              id: index + 1,
              name: reviewerName,
              rating: rating,
              title: review.Title || review.title || `Review from ${reviewerName}`,
              description: review.Review || review.review || '',
              date: date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              }),
              dateObj: date,
              service: service,
              initials: reviewerName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2)
            };
          });
          
          setReviews(formattedReviews);
          setError(null);
        } else {
          setError(data.message || 'No reviews found');
          setReviews([]);
        }
      } else {
        setError('Failed to load reviews');
        setReviews([]);
      }
      
      setLoading(false);
      
    } catch (error) {
      console.error('Error fetching reviews:', error);
      setLoading(false);
      setError('Failed to load reviews. Please try again.');
      setReviews([]);
    }
  };

  useEffect(() => {
    fetchReviews();
    
    // Add scroll event listener for scroll-to-top button
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Reset to page 1 when filter or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilter, sortBy, reviewsPerPage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewReview(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!newReview.name.trim()) errors.name = "Name is required";
    if (!newReview.review.trim()) errors.review = "Review is required";
    if (newReview.email && !/\S+@\S+\.\S+/.test(newReview.email)) {
      errors.email = "Email is invalid";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    
    if (Object.keys(errors).length === 0) {
      setSubmitting(true);
      setError(null);
      
      try {
        const formData = new URLSearchParams();
        formData.append('name', newReview.name);
        formData.append('email', newReview.email || '');
        formData.append('rating', newReview.rating.toString());
        formData.append('service', newReview.service || 'General Service');
        formData.append('review', newReview.review);
        formData.append('title', newReview.title || `Review from ${newReview.name}`);
        
        await fetch(SCRIPT_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formData.toString()
        });
        
        alert('✅ Thank you for your review!');
        
        const initials = newReview.name
          .split(' ')
          .map(n => n[0])
          .join('')
          .toUpperCase()
          .substring(0, 2);
        
        const newReviewObj = {
          id: reviews.length + 1,
          name: newReview.name,
          rating: parseInt(newReview.rating),
          title: newReview.title || `Review from ${newReview.name}`,
          description: newReview.review,
          date: new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          }),
          dateObj: new Date(),
          service: newReview.service || 'General Service',
          initials: initials
        };
        
        setReviews(prev => [newReviewObj, ...prev]);
        
        setNewReview({
          name: '',
          email: '',
          rating: 5,
          service: '',
          title: '',
          review: ''
        });
        
        setFormErrors({});
        
      } catch (error) {
        console.error('Error submitting review:', error);
        setError('Error submitting review. Please try again.');
      } finally {
        setSubmitting(false);
      }
    } else {
      setFormErrors(errors);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="review-stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`review-star ${star <= rating ? 'filled' : ''}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReviewsPerPageChange = (e) => {
    setReviewsPerPage(Number(e.target.value));
  };

  const handleSortChange = (e) => {
    setSortBy(e.target.value);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate some quick stats
  const fiveStarReviews = reviews.filter(r => r.rating === 5).length;
  const recentReviews = reviews.filter(r => {
    const reviewDate = r.dateObj || new Date(r.date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return reviewDate >= thirtyDaysAgo;
  }).length;

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Client Reviews | Raffcodes - What Our Clients Say</title>
        <meta name="description" content="Read authentic client reviews about Raffcodes' IT solutions, web development, system setup, and tech support services in Nairobi, Kenya." />
        <meta name="keywords" content="client reviews, testimonials, IT services reviews, web development reviews, Raffcodes reviews, tech support feedback" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://raffcodes.tech/reviews" />

        {/* Open Graph Tags */}
        <meta property="og:title" content="Client Reviews | Raffcodes - What Our Clients Say" />
        <meta property="og:description" content="Read authentic client reviews about Raffcodes' IT solutions, web development, and tech support services in Nairobi, Kenya." />
        <meta property="og:url" content="https://raffcodes.tech/reviews" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://raffcodes.tech/images/reviews-og.jpg" />
        <meta property="og:site_name" content="Raffcodes" />
        <meta property="og:locale" content="en_KE" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Client Reviews | Raffcodes" />
        <meta name="twitter:description" content="Read authentic client reviews about Raffcodes' IT solutions and tech support services." />
        <meta name="twitter:image" content="https://raffcodes.tech/images/reviews-og.jpg" />
        <meta name="twitter:site" content="@raffcodes" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="page reviews-page">
        <div className="container">
          {/* Header */}
          <section className="reviews-header">
            <span className="header-tag">Testimonials</span>
            <h1>Client Reviews</h1>
            <p className="header-subtitle">
              See what our clients say about our IT solutions and technology services
            </p>
          </section>

          {/* Star Rating System */}
          {reviews.length > 0 && (
            <div className="star-rating-system">
              <h2 className="star-rating-title">⭐ Rating Overview</h2>
              <div className="rating-summary-content">
                <div className="average-rating-block">
                  <span className="average-score">{reviewStats.averageRating.toFixed(1)}</span>
                  {renderStars(Math.round(reviewStats.averageRating))}
                  <p className="average-label">{reviews.length} reviews</p>
                </div>
                <div className="rating-breakdown">
                  {[5, 4, 3, 2, 1].map(rating => (
                    <div key={rating} className="rating-bar-row">
                      <div className="rating-progress-bar-container">
                        <div 
                          className="rating-progress-bar" 
                          style={{ width: `${reviewStats.ratingPercentages[rating]}%` }}
                        />
                      </div>
                      <div className="rating-stars-label">
                        {renderStars(rating)}
                      </div>
                      <span className="rating-percentage-label">{reviewStats.ratingPercentages[rating]}%</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Quick Stats */}
              <div className="reviews-quick-stats">
                <div className="quick-stat">
                  <i className="fas fa-star"></i>
                  <span>{fiveStarReviews} five-star reviews</span>
                </div>
                <div className="quick-stat">
                  <i className="fas fa-calendar-alt"></i>
                  <span>{recentReviews} reviews this month</span>
                </div>
                <div className="quick-stat">
                  <i className="fas fa-comments"></i>
                  <span>{filteredReviews.length} filtered reviews</span>
                </div>
              </div>
            </div>
          )}
          
          {/* Review Form */}
          <div className="review-form-section">
            <h3 className="form-title">Share Your Experience</h3>
            <form onSubmit={handleSubmit} className="review-form-grid">
              <div className="form-group">
                <label>Your Name *</label>
                <input
                  type="text"
                  name="name"
                  value={newReview.name}
                  onChange={handleInputChange}
                  placeholder="John Doe"
                  className={formErrors.name ? 'error' : ''}
                  disabled={submitting}
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>

              <div className="form-group">
                <label>Your Email (Optional)</label>
                <input
                  type="email"
                  name="email"
                  value={newReview.email}
                  onChange={handleInputChange}
                  placeholder="john@example.com"
                  disabled={submitting}
                />
                {formErrors.email && <span className="error-message">{formErrors.email}</span>}
              </div>

              <div className="form-group">
                <label>Rating *</label>
                <select
                  name="rating"
                  value={newReview.rating}
                  onChange={handleInputChange}
                  disabled={submitting}
                >
                  <option value="5">⭐⭐⭐⭐⭐ Excellent</option>
                  <option value="4">⭐⭐⭐⭐ Very Good</option>
                  <option value="3">⭐⭐⭐ Good</option>
                  <option value="2">⭐⭐ Fair</option>
                  <option value="1">⭐ Poor</option>
                </select>
              </div>

              <div className="form-group">
                <label>Service Used</label>
                <select
                  name="service"
                  value={newReview.service}
                  onChange={handleInputChange}
                  disabled={submitting}
                >
                  <option value="">Select a service</option>
                  {services.filter(s => s !== 'All Services').map(service => (
                    <option key={service} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              <div className="form-group full-width">
                <label>Your Review *</label>
                <textarea
                  name="review"
                  value={newReview.review}
                  onChange={handleInputChange}
                  placeholder="Tell us about your experience..."
                  rows="3"
                  className={formErrors.review ? 'error' : ''}
                  disabled={submitting}
                />
                {formErrors.review && <span className="error-message">{formErrors.review}</span>}
              </div>

              <div className="form-group full-width submit-group">
                <button 
                  type="submit" 
                  className="submit-btn" 
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-paper-plane"></i> Submit Review
                    </>
                  )}
                </button>
                
                <p className="form-note">
                  <i className="fas fa-info-circle"></i> Your review will be sent to our Google Sheet.
                </p>
              </div>
            </form>
          </div>

          {/* Reviews List Title */}
          <h2 className="reviews-list-title">Recent Reviews</h2>

          {/* Summary & Sort Bar */}
          {reviews.length > 0 && (
            <div className="reviews-summary-bar">
              <p>
                <i className="fas fa-eye"></i>
                showing {currentReviews.length} of {reviews.length} reviews
              </p>   
               <div className="reviews-sort">
                <label htmlFor="sortBy">Sort by:</label>
                <select id="sortBy" value={sortBy} onChange={handleSortChange}>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="highest">Highest Rated</option>
                  <option value="lowest">Lowest Rated</option>
                  <option value="name">Name (A-Z)</option>
                </select>
                
                <div className="reviews-per-page">
                  <label htmlFor="perPage">Show:</label>
                  <select id="perPage" value={reviewsPerPage} onChange={handleReviewsPerPageChange}>
                    <option value="3">3 per page</option>
                    <option value="5">5 per page</option>
                    <option value="10">10 per page</option>
                    <option value="15">15 per page</option>
                    <option value="20">20 per page</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Filter Buttons */}
          <div className="reviews-filters">
            <div className="filter-buttons">
              {services.map(service => (
                <button
                  key={service}
                  className={`filter-btn ${activeFilter === (service === 'All Services' ? 'all' : service) ? 'active' : ''}`}
                  onClick={() => setActiveFilter(service === 'All Services' ? 'all' : service)}
                >
                  {service}
                </button>
              ))}
            </div>
          </div>

          {/* Error/Loading Messages */}
          {error && (
            <div className="error-message-container">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{error}</p>
              <button onClick={fetchReviews} className="retry-btn">
                <i className="fas fa-redo"></i> Try Again
              </button>
            </div>
          )}

          {loading && (
            <div className="loading-reviews">
              <i className="fas fa-spinner fa-spin"></i>
              <p>Loading reviews...</p>
            </div>
          )}

          {/* Reviews List */}
          {!loading && !error && (
            <>
              <div className="reviews-list-container">
                {currentReviews.length > 0 ? (
                  currentReviews.map((review) => (
                    <div key={review.id} className="review-list-item">
                      {/* Column 1: Avatar & Stars */}
                      <div className="review-avatar-column">
                        <div className="reviewer-avatar-list">
                          {review.initials}
                        </div>
                        <div className="review-avatar-stars">
                          {renderStars(review.rating)}
                        </div>
                      </div>

                      {/* Column 2: Date & Name */}
                      <div className="review-info-column">
                        <div className="review-date-list">{review.date}</div>
                        <h4 className="reviewer-name-list">{review.name}</h4>
                      </div>

                      {/* Column 3: Review Content */}
                      <div className="review-content-column">
                        <p className="review-description-list">{review.description}</p>
                      </div>

                      {/* Column 4 & 5: Service Tag & Report Link */}
                      <div className="review-actions-column">
                        <span className="review-service-tag">{review.service}</span>
                        <span className="review-report-link">
                          <i className="fas fa-flag"></i> Report
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-reviews-message">
                    <i className="far fa-comment-dots"></i>
                    <h4>No reviews found</h4>
                    <p>Try changing your filter or be the first to share your experience!</p>
                  </div>
                )}
              </div>

              {/* Pagination Controls */}
              {filteredReviews.length > 0 && filteredReviews.length > reviewsPerPage && (
                <div className="reviews-pagination">
                  <div className="pagination-info">
                    Page {currentPage} of {totalPages}
                  </div>
                  
                  <div className="pagination-controls">
                    <button 
                      className="page-btn"
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-double-left"></i>
                    </button>
                    
                    <button 
                      className="page-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="fas fa-angle-left"></i>
                    </button>
                    
                    {getPageNumbers().map((page, index) => (
                      page === '...' ? (
                        <span key={`ellipsis-${index}`} className="page-btn ellipsis">...</span>
                      ) : (
                        <button
                          key={page}
                          className={`page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      )
                    ))}
                    
                    <button 
                      className="page-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-angle-right"></i>
                    </button>
                    
                    <button 
                      className="page-btn"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    >
                      <i className="fas fa-angle-double-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Scroll to Top Button */}
        <button 
          className={`scroll-to-top ${showScrollTop ? 'visible' : ''}`}
          onClick={scrollToTop}
          aria-label="Scroll to top"
        >
          <i className="fas fa-chevron-up"></i>
        </button>
      </div>
    </>
  );
};

export default Reviews;